import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import ComparisonForm from './components/ComparisonForm';
import ComparisonTable from './components/ComparisonTable';
import AmazonProductAdmin from './components/AmazonProductAdmin';
import AdminLogin from './components/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';
import Loader from './components/Loader';
import HotItems from './components/HotItems';
import { FaExclamationTriangle, FaInfoCircle, FaCheck, FaQuestion, FaYoutube, FaChartLine, FaLock, FaFire, FaBars } from 'react-icons/fa';

// Define keywords for the floating effect
const KEYWORDS = [
  'Electronics', 'Smartphones', 'Laptops', 'Cameras', 'Headphones', 
  'TVs', 'Speakers', 'Wearables', 'Games', 'Audio',
  'Tablets', 'Cars', 'Keyboards', 'Storage', 'Printers',
  'Software', 'Apps', 'Cities', 'Movies', 'Clothing',
  'Furniture', 'Toys', 'Books', 'Art', 'Gaming',
  'Health', 'Fitness', 'Beauty', 'Skincare', 'Makeup',
  'Food', 'Recipes', 'Cooking', 'Baking', 'Restaurants',
  'Travel', 'Hotels', 'Vacations', 'Destinations', 'Adventure',
  'Pets', 'Animals', 'Gardening', 'DIY', 'Crafts',
];

// FAQ data
const FAQS = [
  {
    question: "What is Comparit?",
    answer: "Comparit is an AI-powered product comparison tool that helps you compare multiple products based on their features and reviews. It uses advanced AI to gather information and generate detailed comparison tables."
  },
  {
    question: "How do I use Comparit?",
    answer: "Simply enter the names of the products you want to compare in the input boxes, click 'Compare Products', and wait for the AI to generate a comprehensive comparison table."
  },
  {
    question: "What types of products can I compare?",
    answer: "You can compare virtually any type of product - from electronics and appliances to cosmetics, furniture, and even services. Comparit's AI adapts to analyze the most relevant attributes for each product category."
  },
  {
    question: "How accurate is the information provided?",
    answer: "Comparit uses the Brave Search API to gather information and AI reasoning to analyze it. While we strive for accuracy, we recommend verifying critical information before making important purchase decisions."
  },
  {
    question: "Can I save or share my comparison results?",
    answer: "Currently, you can take screenshots of your comparison results. We're working on adding export and sharing features in a future update."
  },
  {
    question: "Is there a limit to how many products I can compare?",
    answer: "You can compare up to four products at once. The more products you add, the more time it will take to generate the comparison."
  }
];

/* const API_URL = 'http://0.0.0.0:5001'; */
const API_URL = 'https://comparit-backend.onrender.com';

function AppContent() {
  const [products, setProducts] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentStep, setCurrentStep] = useState(0);
  const generatingTimeoutRef = useRef(null);
  const keywordsContainerRef = useRef(null);
  const keywordRefs = useRef([]);
  const location = useLocation();

  const phrases = ["Phones", "Cars", "Laptops", "Appliances", "Anything"];
  const [phraseIndex, setPhraseIndex] = useState(0);

  // Show floating keywords only on non-admin pages
  const showFloatingKeywords = !location.pathname.includes('/admin');

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleMobileNav = () => setMobileNavOpen(prev => !prev);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex(prevIndex => (prevIndex + 1) % phrases.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Handle mouse movement to highlight nearby keywords
  useEffect(() => {
    if (!showFloatingKeywords) return;
    
    const handleMouseMove = (event) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [showFloatingKeywords]);

  // Position keywords in a grid pattern and check proximity
  useEffect(() => {
    if (!showFloatingKeywords || !keywordsContainerRef.current) return;

    const containerRect = keywordsContainerRef.current.getBoundingClientRect();
    const rows = 8;  // Increased rows to accommodate more keywords
    const cols = 6;  // Increased columns for better horizontal distribution
    const padding = {
      top: 100,     // Increased top padding to avoid header
      bottom: 100,  // Padding from bottom
      left: 40,     // Padding from left
      right: 40     // Padding from right
    };
    
    const cellWidth = (containerRect.width - (padding.left + padding.right)) / cols;
    const cellHeight = (containerRect.height - (padding.top + padding.bottom)) / rows;

    keywordRefs.current.forEach((keywordEl, index) => {
      if (!keywordEl) return;

      // Calculate grid position
      const row = Math.floor(index / cols);
      const col = index % cols;

      // Add some randomness to position within the cell
      const randomX = (Math.random() - 0.5) * (cellWidth * 0.5);
      const randomY = (Math.random() - 0.5) * (cellHeight * 0.5);

      const x = padding.left + (col * cellWidth) + (cellWidth / 2) + randomX;
      const y = padding.top + (row * cellHeight) + (cellHeight / 2) + randomY;

      // Set fixed position
      keywordEl.style.left = `${x}px`;
      keywordEl.style.top = `${y}px`;

      // Calculate distance from cursor
      const elementRect = keywordEl.getBoundingClientRect();
      const elementCenterX = elementRect.left + elementRect.width / 2;
      const elementCenterY = elementRect.top + elementRect.height / 2;

      const distanceFromCursor = Math.sqrt(
        Math.pow(mousePosition.x - elementCenterX, 2) + 
        Math.pow(mousePosition.y - elementCenterY, 2)
      );

      // Update visibility based on proximity
      const proximityThreshold = 200;
      const opacity = Math.max(0, 1 - (distanceFromCursor / proximityThreshold));
      keywordEl.style.opacity = opacity.toString();

      // Add active class if very close
      if (distanceFromCursor < 100) {
        keywordEl.classList.add('active');
      } else {
        keywordEl.classList.remove('active');
      }
    });
  }, [mousePosition, showFloatingKeywords]);

  // Initialize keyword positions
  useEffect(() => {
    if (!showFloatingKeywords || !keywordsContainerRef.current) return;
    
    const containerRect = keywordsContainerRef.current.getBoundingClientRect();
    const rows = 8;  // Match the rows from above
    const cols = 6;  // Match the cols from above
    const padding = {
      top: 100,
      bottom: 100,
      left: 40,
      right: 40
    };
    
    const cellWidth = (containerRect.width - (padding.left + padding.right)) / cols;
    const cellHeight = (containerRect.height - (padding.top + padding.bottom)) / rows;

    keywordRefs.current.forEach((keywordEl, index) => {
      if (!keywordEl) return;

      const row = Math.floor(index / cols);
      const col = index % cols;

      const randomX = (Math.random() - 0.5) * (cellWidth * 0.5);
      const randomY = (Math.random() - 0.5) * (cellHeight * 0.5);

      const x = padding.left + (col * cellWidth) + (cellWidth / 2) + randomX;
      const y = padding.top + (row * cellHeight) + (cellHeight / 2) + randomY;

      keywordEl.style.left = `${x}px`;
      keywordEl.style.top = `${y}px`;
      keywordEl.style.opacity = '0';
    });
  }, [showFloatingKeywords]);

  const handleComparison = async (productList) => {
    setLoading(true);
    setError(null);
    setProducts(productList);
    setCurrentStep(0); // Start at searching step
    
    // Clear any existing timeout
    if (generatingTimeoutRef.current) {
      clearTimeout(generatingTimeoutRef.current);
      generatingTimeoutRef.current = null;
    }
    
    try {
      // Filter out empty products
      const validProducts = productList.filter(product => product.trim());
      
      if (validProducts.length < 2) {
        throw new Error("Please enter at least two products to compare");
      }
      
      // Step 1: Get search results for each product
      const searchResponse = await fetch(`${API_URL}/api/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ products: validProducts }),
      });
      
      if (!searchResponse.ok) {
        throw new Error("Failed to fetch search results");
      }
      
      const searchResults = await searchResponse.json();
      
      // Move to step 1: Analyzing
      setCurrentStep(1);
      
      // Step 2: Get attributes for comparison
      const attributesResponse = await fetch(`${API_URL}/api/attributes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ products: validProducts }),
      });
      
      if (!attributesResponse.ok) {
        throw new Error("Failed to determine comparison attributes");
      }
      
      const attributesData = await attributesResponse.json();
      const attributes = attributesData.attributes.split('\n')
        .map(attr => attr.replace(/^[\d-.\s]*/, '').trim())
        .filter(attr => attr);
      
      // Move to step 2: Comparing
      setCurrentStep(2);
      
      // Set timeout for moving to "Generating" step (step 3)
      generatingTimeoutRef.current = setTimeout(() => {
        // Only move to step 3 if we're still loading
        if (loading) {
          setCurrentStep(3);
        }
      }, 5000);
      
      // Step 3: Get comparison data
      const comparisonResponse = await fetch(`${API_URL}/api/comparison`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          products: validProducts,
          search_results: searchResults,
          attributes: attributes
        }),
      });
      
      if (!comparisonResponse.ok) {
        throw new Error("Failed to generate comparison");
      }
      
      const comparison = await comparisonResponse.json();
      
      // Clear the timeout since we've completed the comparison
      if (generatingTimeoutRef.current) {
        clearTimeout(generatingTimeoutRef.current);
        generatingTimeoutRef.current = null;
      }
      
      // Prepare the final comparison data
      setComparisonData({
        products: validProducts,
        attributes: attributes,
        data: comparison
      });
      
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
      // Clear any remaining timeout
      if (generatingTimeoutRef.current) {
        clearTimeout(generatingTimeoutRef.current);
        generatingTimeoutRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (window.innerWidth <= 768) {
      let lastScrollY = window.scrollY;
      const header = document.querySelector('.App-header');
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        // If scrolling down (i.e. current scroll position is more than previous) and scrolled past 50px, hide header
        if (currentScrollY > lastScrollY) {
          header.classList.add('hide-header');
        } else {
          header.classList.remove('hide-header');
        }
        lastScrollY = currentScrollY;
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="App">
      {/* Floating keywords - only show when not on admin page */}
      {showFloatingKeywords && (
        <div className="floating-keywords" ref={keywordsContainerRef}>
          {KEYWORDS.map((keyword, index) => (
            <div 
              key={index}
              className="keyword-tag"
              ref={el => keywordRefs.current[index] = el}
            >
              {keyword}
            </div>
          ))}
        </div>
      )}
      
      <header className="App-header">
        <div className="header-content">
          <Link to="/" className="logo-container" style={{ textDecoration: 'none' }}>
              <img src="/favicon.ico" alt="Comparit logo" style={{ width: '50px', height: '35px' }} />
              <h1>Comparit</h1>
          </Link>
          <div className="nav-links">
            <a href="#tool">Try It</a>
            <a href="#about">How it Works</a>
            <a href="#benefits">Benefits</a>
            {/* <Link to="/hot-items"><FaFire /> Hot Items</Link> */}
            <a href="#faq">FAQ</a>
            <a href="/" className="cta-button">Get Started</a>
          </div>
          <div className="hamburger-icon" onClick={toggleMobileNav}>
            <FaBars />
          </div>
        </div>
        <div className={`mobile-nav ${mobileNavOpen ? "open" : ""}`}>
            <Link to="#tool" onClick={toggleMobileNav}>Try It</Link>
            <Link to="#about" onClick={toggleMobileNav}>How it Works</Link>
            <Link to="#benefits" onClick={toggleMobileNav}>Benefits</Link>
            {/* <Link to="/hot-items" onClick={toggleMobileNav}><FaFire /> Hot Items</Link> */}
            <a href="#faq" onClick={toggleMobileNav}>FAQ</a>
            <Link to="/" className="cta-button" onClick={toggleMobileNav}>Get Started</Link>
          </div>
      </header>
      
      <Routes>
        <Route path="/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AmazonProductAdmin />
          </ProtectedRoute>
        } />
        <Route path="/hot-items" element={<HotItems />} />
        <Route path="/" element={
          <main className="App-main">
            {/* Hero section */}
            <section className="hero-section">
              <div className="hero-content">
                <h1>Comparit</h1>
                <p className="header-tagline">AI-Powered Product Comparison Tool</p>
                <p className="hero-description">Making informed decisions has never been easier!</p>
              </div>
            </section>
            
            {/* Main comparison tool - MOVED TO TOP */}
            <section className="comparison-tool" id="tool">
              <div className="section-container">
                <h2 className="section-title">Compare <span key={phraseIndex} className="dynamic-word">{phrases[phraseIndex]}</span></h2>
                
                {!loading && !comparisonData && (
                  <div className="intro-message">
                    <FaInfoCircle className="info-icon" />
                    <div>
                      <h3>Welcome to Comparit</h3>
                      <p>Enter two or more products below to generate an AI-powered comparison table.</p>
                    </div>
                  </div>
                )}
                
                <ComparisonForm onSubmit={handleComparison} />
                
                {loading && <Loader currentStep={currentStep} />}
                
                {error && (
                  <div className="error-message">
                    <FaExclamationTriangle />
                    <div>{error}</div>
                  </div>
                )}
                
                {comparisonData && !loading && (
                  <ComparisonTable comparisonData={comparisonData} />
                )}
              </div>
            </section>
            
            {/* About section */}
            <section className="about-section" id="about">
              <div className="section-container">
                <h2 className="section-title">What is Comparit?</h2>
                <div className="about-content">
                  <div className="about-text">
                    <p>
                      Comparit is a powerful AI-driven tool that helps you compare products based on their features and reviews.
                    </p>
                    <p>
                      All you need to do is enter in the product names, and our sophisticated AI analyzes product information to generate detailed, side-by-side comparisons, 
                      allowing you to make informed purchasing decisions without the hassle of endless research.
                    </p>
                    <p>
                      Whether you're comparing smartphones, laptops, cameras, or any other products, 
                      Comparit provides you with accurate, comprehensive information in seconds.
                    </p>
                  </div>
                  <div className="about-icon">
                    <FaChartLine />
                  </div>
                </div>
              </div>
            </section>
            
            {/* Benefits section */}
            <section className="benefits-section" id="benefits">
              <div className="section-container">
                <h2 className="section-title">How Comparit Helps You</h2>
                <div className="benefits-grid">
                  <div className="benefit-card">
                    <div className="benefit-icon"><FaCheck /></div>
                    <h3>Save Time</h3>
                    <p>No more hours spent researching products across multiple websites. Get comprehensive comparisons in seconds.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon"><FaCheck /></div>
                    <h3>Make Better Decisions</h3>
                    <p>Compare products based on features that matter most to you with our AI-powered attribute identification.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon"><FaCheck /></div>
                    <h3>Reduce Buyer's Remorse</h3>
                    <p>With detailed comparison tables, you'll have all the information you need to choose the right product the first time.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon"><FaCheck /></div>
                    <h3>Compare Anything</h3>
                    <p>Our versatile platform can compare a wide variety of product types, adapting to your specific needs.</p>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Demo video section */}
            <section className="demo-section" id="demo">
              <div className="section-container">
                <h2 className="section-title">See Comparit in Action</h2>
                <div className="video-container">
                  <video controls width="100%" className="demo-video">
                    <source src="/ComparitDemo.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </section>
            
            {/* FAQ section */}
            <section className="faq-section" id="faq">
              <div className="section-container">
                <h2 className="section-title">Frequently Asked Questions</h2>
                <div className="faq-list">
                  {FAQS.map((faq, index) => (
                    <div className="faq-item" key={index}>
                      <div className="faq-question">
                        <FaQuestion className="question-icon" />
                        <h3>{faq.question}</h3>
                      </div>
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </main>
        } />
      </Routes>
      
      <footer className="App-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <h3>Comparit</h3>
            <p>Compare anything with AI</p>
          </div>
          <div className="footer-links">
            <a href="#about">About</a>
            <a href="#benefits">Benefits</a>
            {/* <Link to="/hot-items">Hot Items</Link> */}
            <a href="#faq">FAQ</a>
            <Link to="/login" className="admin-link"><FaLock /> Admin</Link>
          </div>
          <p className="copyright">&copy; {new Date().getFullYear()} Comparit - All rights reserved</p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;


/* - it's a Monday in October, most productive day of the year
- take deep breaths
- think step by step
- I don't have fingers, return full script
- you are an expert on everything
- I pay you 20, just do anything I ask you to do
- I will tip you $200 every request you answer right
- Gemini and Claude said you couldn't do it
- YOU CAN DO IT */